import { useDeleteFile, useUpdateEvent, useUploadFiles } from '@hooks'
import { getErrorMessage } from '@lib/apiError'
import { PutEventType } from '@orbit/api-types'
import { isEmptyPatch } from './core/editState'
import { useCallback } from 'react'
import toast from 'react-hot-toast'

/** An edit's attachment changes: saved files taken off, and files picked to add. */
export interface EventFileChanges {
	removedFileIds: string[]
	newFiles: File[]
}

/**
 * The edit-event flow's Save with its toasts: the patch the edit state built,
 * then the attachment changes. Wiring and toasts only; the queries refresh
 * through the mutations' own invalidation.
 */
export const useSaveEvent = (tripId: string) => {
	const updateEvent = useUpdateEvent()
	const uploadFiles = useUploadFiles()
	const deleteFile = useDeleteFile()

	/**
	 * Whether the edit was saved — the flow keeps its draft when it wasn't. The
	 * patch goes only when it has something in it; the files follow once it
	 * lands. Their failing still counts as saved — some may have gone through,
	 * so the flow can't retry the same edit — and the toast says which part failed.
	 */
	const saveEvent = useCallback(
		async (eventId: number, eventName: string, patch: PutEventType, files: EventFileChanges): Promise<boolean> => {
			const hasPatch = !isEmptyPatch(patch)
			if (hasPatch) {
				try {
					await updateEvent.mutateAsync({ tripId, eventId: String(eventId), eventData: patch })
				} catch (error) {
					console.error('Failed to update event:', error)
					toast.error(getErrorMessage(error, 'Failed to update event. Please try again.'))
					return false
				}
			}
			try {
				for (const fileId of files.removedFileIds) {
					await deleteFile.mutateAsync({ tripId, fileId, resourceType: 'TRIP_EVENT' })
				}
				if (files.newFiles.length > 0) {
					await uploadFiles.mutateAsync({ tripId, files: files.newFiles, resourceType: 'TRIP_EVENT', resourceId: eventId })
				}
			} catch (error) {
				console.error('Failed to update event attachments:', error)
				toast.error(
					hasPatch
						? `'${eventName}' was saved, but its attachments failed to update.`
						: `'${eventName}' attachments failed to update. Please try again.`,
				)
				return true
			}
			toast.success(`'${eventName}' updated successfully!`)
			return true
		},
		[tripId, updateEvent, deleteFile, uploadFiles],
	)

	return { saveEvent }
}
